import { useContext, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { Divider } from '@mui/material';

import { RootStoreContext } from 'index';
import useForm from 'hooks/useForm';
import TextInput from 'components/common/TextInput/TextInput';
import Button from 'components/common/Button/Button';
import Checkbox from 'components/common/Checkbox/Checkbox';
import { notify } from 'components/common/Toast/toast.helpers';
import { ToastMessage } from 'components/common/Toast/toast.enums';
import { RoleDialogType } from 'components/roles/roles.enums';

import { RoleDto, CreateRoleRequestDto, UpdateRoleRequestDto } from '../../roles.dto';
import {
  ROLE_ALREADY_EXISTS_ERROR,
  ROLE_NAME_REQUIRED_ERROR,
  ROLE_PERMISSION_REQUIRED_ERROR,
  SKELETON_PERMISSIONS_COUNT,
} from '../../roles.constants';
import styles from './RoleForm.module.scss';
import PermissionSkeletonItem from './PermissionSkeletonItem/PermissionSkeletonItem';

interface RoleFormProps {
  roleDto?: RoleDto;
  onClose: () => void;
  dialogType: RoleDialogType;
}

const skeletonRows = Array.from(Array(SKELETON_PERMISSIONS_COUNT).keys());

const RoleForm = ({ roleDto, onClose, dialogType }: RoleFormProps) => {
  const { permissionsStore, rolesStore } = useContext(RootStoreContext);
  const { permissions, isLoading } = permissionsStore;
  const [isSaveButtonLoading, setIsSaveButtonLoading] = useState<boolean>(false);
  const isReadOnly = dialogType === RoleDialogType.View;

  const validateRole = (fieldName: string, value: any) => {
    if (fieldName === 'name') {
      if (!value) {
        return ROLE_NAME_REQUIRED_ERROR;
      }

      const isExists = rolesStore.roles.some((role: RoleDto) => {
        const isSameName = role.name.toLowerCase() === String(value).toLowerCase();
        const isSelf = roleDto ? role.id === roleDto.id : false;

        return isSameName && !isSelf;
      });

      if (isExists) {
        return ROLE_ALREADY_EXISTS_ERROR;
      }

      return '';
    }

    if (fieldName === 'permissionIds') {
      return !!value.length ? '' : ROLE_PERMISSION_REQUIRED_ERROR;
    }

    return '';
  };

  const { values, errors, setValue, handleSubmit } = useForm({
    defaultValues: {
      name: roleDto?.name ?? '',
      permissionIds: roleDto?.permissionIds ?? [],
    },
    handleValidation: validateRole,
  });

  const togglePermission = (permissionId: number) => {
    const isChecked = values.permissionIds.includes(permissionId);
    const newIds = isChecked
      ? values.permissionIds.filter((id: number) => id !== permissionId)
      : [...values.permissionIds, permissionId];

    setValue('permissionIds', newIds);
  };

  const onFormSubmit = handleSubmit(async () => {
    setIsSaveButtonLoading(true);

    try {
      if (roleDto) {
        const payload: UpdateRoleRequestDto = {
          name: values.name,
          permissionIds: values.permissionIds,
        };

        await rolesStore.updateRole(roleDto.id, payload);
        notify(ToastMessage.RoleUpdated, 'success');
      } else {
        const payload: CreateRoleRequestDto = {
          name: values.name,
          permissionIds: values.permissionIds,
        };

        await rolesStore.createRole(payload);
        notify(ToastMessage.RoleCreated, 'success');
      }

      onClose();
    } catch (error: any) {
      notify(error.message, 'error');
    } finally {
      setIsSaveButtonLoading(false);
    }
  });

  return (
    <>
      <div className={styles.inputWrap}>
        <TextInput
          labelTitle={'Role*'}
          value={values.name}
          onChange={(event) => setValue('name', event.target.value)}
          required
          errorMessage={errors.name}
          disabled={isReadOnly}
        />
      </div>

      <div className={styles.permissionsWrap}>
        <Divider />
        {isLoading
          ? skeletonRows.map((rowIndex: number) => <PermissionSkeletonItem key={rowIndex} />)
          : permissions.map((permission) => {
              const isChecked = values.permissionIds.includes(permission.id);

              return (
                <div key={permission.id} className={styles.permission}>
                  <Checkbox
                    name={permission.id.toString()}
                    label={permission.name}
                    isChecked={isChecked}
                    onChange={() => togglePermission(permission.id)}
                    disabled={isReadOnly}
                  />
                  <Divider className={styles.divider} />
                </div>
              );
            })}
        {errors.permissionIds && <div className={styles.groupError}>{errors.permissionIds}</div>}
      </div>

      <div className={styles.buttonSave}>
        {isReadOnly ? (
          <Button onClick={onClose} variant="white">
            Return to Roles
          </Button>
        ) : (
          <Button onClick={onFormSubmit} isLoading={isSaveButtonLoading}>
            Save Role
          </Button>
        )}
      </div>
    </>
  );
};

export default observer(RoleForm);
